import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { ArrowLeft, ArrowRight, Save, CheckCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const POLICY_TYPES = [
  { id: 'life_insurance', label: 'Life Insurance' },
  { id: 'iul', label: 'IUL (Indexed Universal Life)' },
  { id: 'annuity', label: 'Annuity' },
  { id: 'retirement', label: 'Retirement Planning' },
  { id: 'long_term_care', label: 'Long-Term Care' },
  { id: 'estate_planning', label: 'Estate Planning' }
];

const INCOME_RANGES = [
  'Under $25,000',
  '$25,000 - $50,000',
  '$50,000 - $75,000',
  '$75,000 - $100,000',
  '$100,000 - $150,000',
  '$150,000 - $250,000',
  'Over $250,000',
  'Prefer not to say'
];

const CustomerProfile = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [step, setStep] = useState(1);
  
  const [formData, setFormData] = useState({
    full_legal_name: '',
    date_of_birth: '',
    address: '',
    city: '',
    state: '',
    zip_code: '',
    country: 'USA',
    phone: '',
    email: user?.email || '',
    occupation: '',
    income_range: '',
    beneficiary_name: '',
    beneficiary_relationship: '',
    beneficiary_contact: '',
    existing_insurance: '',
    policy_interests: [],
    consent_agreed: false,
    privacy_policy_agreed: false,
    terms_agreed: false
  });

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const response = await axios.get(`${API}/customer/profile`, { withCredentials: true });
      if (response.data && Object.keys(response.data).length > 0) {
        setFormData(prev => ({ ...prev, ...response.data }));
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handlePolicyInterestChange = (policyId, checked) => {
    setFormData(prev => ({
      ...prev,
      policy_interests: checked
        ? [...prev.policy_interests, policyId]
        : prev.policy_interests.filter(id => id !== policyId)
    }));
  };

  const handleCheckboxChange = (name, checked) => {
    setFormData(prev => ({ ...prev, [name]: checked }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.consent_agreed || !formData.privacy_policy_agreed || !formData.terms_agreed) {
      toast.error('You must agree to all terms and conditions');
      return;
    }

    setSaving(true);
    try {
      await axios.post(`${API}/customer/profile`, formData, { withCredentials: true });
      toast.success('Profile saved successfully!');
      navigate('/portal/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save profile');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-slate-200 border-t-amber-600 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/portal/dashboard" className="flex items-center gap-2 text-slate-600 hover:text-slate-900">
              <ArrowLeft className="w-5 h-5" />
              Back to Dashboard
            </Link>
            <span className="text-sm text-slate-500">Step {step} of 3</span>
          </div>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Progress Bar */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-2">
            {['Personal Info', 'Financial Details', 'Review & Submit'].map((label, index) => (
              <div key={label} className="flex items-center">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                  step > index + 1 ? 'bg-green-500 text-white' :
                  step === index + 1 ? 'bg-navy text-white' : 'bg-slate-200 text-slate-500'
                }`}>
                  {step > index + 1 ? <CheckCircle className="w-5 h-5" /> : index + 1}
                </div>
                {index < 2 && <div className={`w-24 sm:w-32 h-1 mx-2 ${step > index + 1 ? 'bg-green-500' : 'bg-slate-200'}`} />}
              </div>
            ))}
          </div>
        </div>

        <motion.div
          key={step}
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          className="bg-white rounded-2xl shadow-sm border border-slate-100 p-8"
        >
          <form onSubmit={handleSubmit}>
            {/* Step 1: Personal Information */}
            {step === 1 && (
              <>
                <h2 className="text-2xl font-bold text-navy font-serif mb-6">Personal Information</h2>
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="md:col-span-2 space-y-2">
                    <Label htmlFor="full_legal_name">Full Legal Name *</Label>
                    <Input
                      id="full_legal_name"
                      name="full_legal_name"
                      value={formData.full_legal_name}
                      onChange={handleChange}
                      required
                      className="h-12"
                      data-testid="profile-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="date_of_birth">Date of Birth *</Label>
                    <Input
                      id="date_of_birth"
                      name="date_of_birth"
                      type="date"
                      value={formData.date_of_birth}
                      onChange={handleChange}
                      required
                      className="h-12"
                      data-testid="profile-dob-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="phone">Phone *</Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      className="h-12"
                      data-testid="profile-phone-input"
                    />
                  </div>
                  <div className="md:col-span-2 space-y-2">
                    <Label htmlFor="email">Email *</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="h-12"
                      data-testid="profile-email-input"
                    />
                  </div>
                  <div className="md:col-span-2 space-y-2">
                    <Label htmlFor="address">Street Address *</Label>
                    <Input
                      id="address"
                      name="address"
                      value={formData.address}
                      onChange={handleChange}
                      required
                      className="h-12"
                      data-testid="profile-address-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="city">City *</Label>
                    <Input
                      id="city"
                      name="city"
                      value={formData.city}
                      onChange={handleChange}
                      required
                      className="h-12"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="state">State/Province *</Label>
                    <Input
                      id="state"
                      name="state"
                      value={formData.state}
                      onChange={handleChange}
                      required
                      className="h-12"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="zip_code">ZIP/Postal Code *</Label>
                    <Input
                      id="zip_code"
                      name="zip_code"
                      value={formData.zip_code}
                      onChange={handleChange}
                      required
                      className="h-12"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="country">Country *</Label>
                    <Select value={formData.country} onValueChange={(v) => handleSelectChange('country', v)}>
                      <SelectTrigger className="h-12">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="USA">United States</SelectItem>
                        <SelectItem value="Canada">Canada</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </>
            )}

            {/* Step 2: Financial Details */}
            {step === 2 && (
              <>
                <h2 className="text-2xl font-bold text-navy font-serif mb-6">Financial & Insurance Details</h2>
                <div className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="occupation">Occupation</Label>
                      <Input
                        id="occupation"
                        name="occupation"
                        value={formData.occupation}
                        onChange={handleChange}
                        className="h-12"
                        data-testid="profile-occupation-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="income_range">Annual Income Range</Label>
                      <Select value={formData.income_range} onValueChange={(v) => handleSelectChange('income_range', v)}>
                        <SelectTrigger className="h-12" data-testid="profile-income-select">
                          <SelectValue placeholder="Select range" />
                        </SelectTrigger>
                        <SelectContent>
                          {INCOME_RANGES.map(range => (
                            <SelectItem key={range} value={range}>{range}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <Label>Policy Types Interested In</Label>
                    <div className="grid sm:grid-cols-2 gap-3">
                      {POLICY_TYPES.map(policy => (
                        <label key={policy.id} className="flex items-center gap-3 p-3 border border-slate-200 rounded-lg hover:bg-slate-50 cursor-pointer">
                          <Checkbox
                            checked={formData.policy_interests.includes(policy.id)}
                            onCheckedChange={(checked) => handlePolicyInterestChange(policy.id, checked)}
                            data-testid={`policy-${policy.id}`}
                          />
                          <span className="text-sm">{policy.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="existing_insurance">Existing Insurance Policies (if any)</Label>
                    <Textarea
                      id="existing_insurance"
                      name="existing_insurance"
                      value={formData.existing_insurance}
                      onChange={handleChange}
                      placeholder="Describe any existing life insurance, health insurance, or other policies..."
                      className="min-h-[100px]"
                      data-testid="profile-existing-insurance"
                    />
                  </div>

                  <div className="border-t border-slate-200 pt-6">
                    <h3 className="text-lg font-semibold text-navy mb-4">Beneficiary Information</h3>
                    <div className="grid md:grid-cols-3 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="beneficiary_name">Beneficiary Name</Label>
                        <Input
                          id="beneficiary_name"
                          name="beneficiary_name"
                          value={formData.beneficiary_name}
                          onChange={handleChange}
                          className="h-12"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="beneficiary_relationship">Relationship</Label>
                        <Input
                          id="beneficiary_relationship"
                          name="beneficiary_relationship"
                          value={formData.beneficiary_relationship}
                          onChange={handleChange}
                          placeholder="e.g., Spouse, Child"
                          className="h-12"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="beneficiary_contact">Contact Info</Label>
                        <Input
                          id="beneficiary_contact"
                          name="beneficiary_contact"
                          value={formData.beneficiary_contact}
                          onChange={handleChange}
                          placeholder="Phone or Email"
                          className="h-12"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}

            {/* Step 3: Review & Submit */}
            {step === 3 && (
              <>
                <h2 className="text-2xl font-bold text-navy font-serif mb-6">Review & Submit</h2>
                
                {/* Summary */}
                <div className="bg-slate-50 rounded-xl p-6 mb-6">
                  <h3 className="font-semibold text-navy mb-4">Application Summary</h3>
                  <dl className="grid sm:grid-cols-2 gap-4 text-sm">
                    <div>
                      <dt className="text-slate-500">Full Name</dt>
                      <dd className="font-medium">{formData.full_legal_name}</dd>
                    </div>
                    <div>
                      <dt className="text-slate-500">Date of Birth</dt>
                      <dd className="font-medium">{formData.date_of_birth}</dd>
                    </div>
                    <div>
                      <dt className="text-slate-500">Email</dt>
                      <dd className="font-medium">{formData.email}</dd>
                    </div>
                    <div>
                      <dt className="text-slate-500">Phone</dt>
                      <dd className="font-medium">{formData.phone}</dd>
                    </div>
                    <div className="sm:col-span-2">
                      <dt className="text-slate-500">Address</dt>
                      <dd className="font-medium">{formData.address}, {formData.city}, {formData.state} {formData.zip_code}</dd>
                    </div>
                    <div className="sm:col-span-2">
                      <dt className="text-slate-500">Interested Policies</dt>
                      <dd className="font-medium">
                        {formData.policy_interests.map(id => POLICY_TYPES.find(p => p.id === id)?.label).join(', ') || 'None selected'}
                      </dd>
                    </div>
                  </dl>
                </div>

                {/* Agreements */}
                <div className="space-y-4">
                  <label className="flex items-start gap-3 p-4 border border-slate-200 rounded-lg">
                    <Checkbox
                      checked={formData.consent_agreed}
                      onCheckedChange={(checked) => handleCheckboxChange('consent_agreed', checked)}
                      className="mt-0.5"
                      data-testid="consent-checkbox"
                    />
                    <span className="text-sm text-slate-600">
                      I consent to WealthConnexs collecting and processing my personal information for insurance application purposes. *
                    </span>
                  </label>
                  <label className="flex items-start gap-3 p-4 border border-slate-200 rounded-lg">
                    <Checkbox
                      checked={formData.privacy_policy_agreed}
                      onCheckedChange={(checked) => handleCheckboxChange('privacy_policy_agreed', checked)}
                      className="mt-0.5"
                      data-testid="privacy-checkbox"
                    />
                    <span className="text-sm text-slate-600">
                      I have read and agree to the <a href="#" className="text-amber-600 hover:underline">Privacy Policy</a>. *
                    </span>
                  </label>
                  <label className="flex items-start gap-3 p-4 border border-slate-200 rounded-lg">
                    <Checkbox
                      checked={formData.terms_agreed}
                      onCheckedChange={(checked) => handleCheckboxChange('terms_agreed', checked)}
                      className="mt-0.5"
                      data-testid="terms-checkbox"
                    />
                    <span className="text-sm text-slate-600">
                      I agree to the <a href="#" className="text-amber-600 hover:underline">Terms of Service</a> and confirm that all information provided is accurate. *
                    </span>
                  </label>
                </div>
              </>
            )}

            {/* Navigation Buttons */}
            <div className="flex justify-between mt-8 pt-6 border-t border-slate-200">
              {step > 1 ? (
                <Button type="button" variant="outline" onClick={() => setStep(step - 1)} className="rounded-full">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Previous
                </Button>
              ) : (
                <div />
              )}
              
              {step < 3 ? (
                <Button type="button" onClick={() => setStep(step + 1)} className="bg-navy hover:bg-slate-800 text-white rounded-full">
                  Next
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              ) : (
                <Button 
                  type="submit" 
                  disabled={saving || !formData.consent_agreed || !formData.privacy_policy_agreed || !formData.terms_agreed}
                  className="bg-amber-600 hover:bg-amber-700 text-white rounded-full"
                  data-testid="submit-profile-btn"
                >
                  {saving ? 'Submitting...' : 'Submit Application'}
                  <Save className="w-4 h-4 ml-2" />
                </Button>
              )}
            </div>
          </form>
        </motion.div>
      </div>
    </div>
  );
};

export default CustomerProfile;
